<script setup lang="ts">
import type { ServicesTotals } from '@/composables/jobEdit/useJobEditProductsServices';
import type { JobEditProductItem, JobEditServiceItem } from '@/models/jobEdit';

import { computed, ref } from 'vue';

import JobEditProductsFieldset from '@/components/fieldsets/JobEditProductsFieldset.vue';
import JobEditServicesFieldset from '@/components/fieldsets/JobEditServicesFieldset.vue';
import JobEditAccordionSection from '@/components/sections/JobEditAccordionSection.vue';
import { useJobEditProductsServices } from '@/composables/jobEdit/useJobEditProductsServices';
import { useJobEditStore } from '@/stores/useJobEditStore';

export interface Props {
  pickupStop?: number;
  deliveryStop?: number;
  stopNumber?: number;
  initialProducts?: JobEditProductItem[];
  initialServices?: JobEditServiceItem[];
  initialInstructions?: string;
  jobTotal?: ServicesTotals;
}

const props = withDefaults(defineProps<Props>(), {
  pickupStop: 1,
  deliveryStop: 2,
  stopNumber: 1,
  initialProducts: () => [],
  initialServices: () => [],
  initialInstructions: '',
  jobTotal: undefined,
});

const emit = defineEmits<{
  'update:stopTotal': [value: ServicesTotals];
}>();

const {
  formData,
  isEditing,
  isLoading,
  startEditing,
  saveEditing,
  cancelEditing,
  stopTotal,
} = useJobEditProductsServices(
  props.stopNumber,
  () => props.initialProducts,
  () => props.initialServices,
  () => props.initialInstructions,
  (total) => {
    emit('update:stopTotal', total);
  },
);

const jobEditStore = useJobEditStore();

const productsCount = computed(() => formData.products.length);
const jobTotalFee = computed(() =>
  props.jobTotal !== undefined
    ? props.jobTotal.fee + props.jobTotal.gst
    : undefined,
);

const productsFieldsetRef = ref<InstanceType<
  typeof JobEditProductsFieldset
> | null>(null);
const servicesFieldsetRef = ref<InstanceType<
  typeof JobEditServicesFieldset
> | null>(null);

function handleAddProduct(): void {
  productsFieldsetRef.value?.addProduct();
}

function handleAddService(): void {
  servicesFieldsetRef.value?.addService();
}

async function handleSave(): Promise<void> {
  try {
    await saveEditing();
  } catch {
    // Error is already handled: fetcher logs to Sentry + shows notification,
    // useJobEditSectionBase opens a "conflict" dialog
  }
}

async function handleCancel(): Promise<void> {
  try {
    await cancelEditing();
  } catch {
    // unlock fetcher already shows the notification; section stays in editing
    // mode so the user can retry cancel or save
  }
}
</script>

<template>
  <JobEditAccordionSection
    :class="[
      $style.jobEditProductsServicesSection,
      { [$style.editing]: isEditing },
    ]"
    :title="`PRODUCTS - STOP ${props.pickupStop} TO STOP ${props.deliveryStop}`"
    :is-editing="isEditing"
    :is-loading="isLoading"
    editable
    data-testid="jobEditProductsServicesSection"
    @cancel="handleCancel"
    @save="handleSave"
    @edit-btn-click="startEditing"
  >
    <template #collapsedContent>
      <span :class="$style.collapsedMeta">Products: {{ productsCount }}</span>
      <span v-if="jobTotalFee !== undefined" :class="$style.collapsedMeta">
        Job total: {{ jobTotalFee.toFixed(2) }}
      </span>
    </template>

    <div :class="$style.content">
      <div :class="$style.subsection">
        <div :class="$style.subsectionHeader">
          <div :class="$style.subsectionTitleWrapper">
            <v-icon :class="$style.subsectionIcon" size="20"
              >mdi-package-variant</v-icon
            >
            <h4 :class="$style.subsectionTitle">PRODUCTS</h4>
          </div>
          <v-btn
            v-if="isEditing"
            prepend-icon="mdi-plus"
            variant="text"
            color="primary"
            size="small"
            @click="handleAddProduct"
          >
            ADD GOOD
          </v-btn>
        </div>
        <JobEditProductsFieldset
          ref="productsFieldsetRef"
          v-model="formData.products"
          :readonly="!isEditing"
        />
      </div>

      <div :class="$style.subsection">
        <div :class="$style.subsectionHeader">
          <div :class="$style.subsectionTitleWrapper">
            <v-icon :class="$style.subsectionIcon" size="20">mdi-cog</v-icon>
            <h4 :class="$style.subsectionTitle">SERVICES</h4>
          </div>
          <div :class="$style.subsectionActions">
            <v-btn
              v-if="isEditing"
              prepend-icon="mdi-plus"
              variant="text"
              color="primary"
              size="small"
              @click="handleAddService"
            >
              ADD SERVICE
            </v-btn>
            <v-btn icon="mdi-dots-vertical" variant="text" size="small" />
          </div>
        </div>
        <JobEditServicesFieldset
          ref="servicesFieldsetRef"
          v-model="formData.services"
          :readonly="!isEditing"
          :job-id="jobEditStore.jobId ?? undefined"
          :job-date="jobEditStore.jobDate ?? undefined"
          :leg-number="props.stopNumber"
          :stop-total="stopTotal"
          :job-total="props.jobTotal"
        />
      </div>

      <div :class="$style.subsection">
        <div :class="$style.subsectionHeader">
          <div :class="$style.subsectionTitleWrapper">
            <v-icon :class="$style.subsectionIcon" size="20"
              >mdi-note-text-outline</v-icon
            >
            <h4 :class="$style.subsectionTitle">INSTRUCTIONS</h4>
          </div>
        </div>
        <div :class="$style.instructionsContent">
          <div
            v-if="!isEditing && !formData.instructions"
            :class="$style.instructionsEmpty"
          >
            No instructions yet
          </div>
          <div
            v-else-if="!isEditing && formData.instructions"
            :class="$style.instructionsText"
          >
            {{ formData.instructions }}
          </div>
          <v-textarea
            v-else
            v-model="formData.instructions"
            :maxlength="200"
            counter
            auto-grow
            rows="3"
            variant="outlined"
            hide-details="auto"
            placeholder="Add instructions here"
            :class="$style.instructionsTextarea"
          />
        </div>
      </div>
    </div>
  </JobEditAccordionSection>
</template>

<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';

.jobEditProductsServicesSection {
  position: relative;
  transition:
    box-shadow 0.3s ease,
    border-color 0.3s ease;
}

.editing {
  border-color: colors.$color-brand-500;
  box-shadow: 0 0 0 2px colors.$color-brand-100;
  z-index: 100;
}

.content {
  display: flex;
  flex-direction: column;
  gap: 32px;
  position: relative;
}

.subsection {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.subsectionHeader {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.subsectionTitleWrapper {
  display: flex;
  align-items: center;
  gap: 8px;
}

.subsectionActions {
  display: flex;
  align-items: center;
  gap: 4px;
}

.subsectionIcon {
  color: colors.$color-brand-500;
}

.subsectionTitle {
  margin: 0;
  font-size: 0.875rem;
  font-weight: 600;
  color: colors.$color-text-primary;
  letter-spacing: 0.5px;
}

.collapsedMeta {
  font-size: 0.8125rem;
  color: colors.$color-text-secondary;
  padding: 0 12px;
  border-left: 1px solid colors.$color-gray-200;
}

.instructionsContent {
  max-width: 600px;
}

.instructionsTextarea {
  :global {
    .v-field__outline {
      --v-field-border-width: 1px !important;
    }
  }
}

.instructionsEmpty {
  border: 1px solid colors.$color-gray-200;
  border-radius: 4px;
  padding: 12px;
  font-size: 0.875rem;
  color: colors.$color-text-secondary;
}

.instructionsText {
  border: 1px solid colors.$color-gray-200;
  border-radius: 4px;
  padding: 12px;
  font-size: 0.875rem;
  color: colors.$color-text-primary;
}
</style>
